export type Language = 'en' | 'fr';

export const LANGUAGES: { value: Language; label: string; short: string }[] = [
  { value: 'en', label: 'English', short: 'EN' },
  { value: 'fr', label: 'Français', short: 'FR' },
];

type TranslationDict = {
  [section: string]: Record<string, string>;
};

export const translations: Record<Language, TranslationDict> = {
  en: {
    meta: {
      homeTitle: 'Full-Stack Web Developer in Montreal',
      skipToContent: 'Skip to content',
    },
    header: {
      brandAria: 'Mohame Roach — home',
      primaryNavAria: 'Primary',
      mobileNavAria: 'Mobile primary',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      projects: 'Projects',
      blog: 'Blog',
      resume: 'Resume',
      faq: 'FAQ',
      contact: 'Contact',
      admin: 'Admin',
      switchLanguageTo: 'Switch language to {lang}',
      languageGroup: 'Language',
    },
    hero: {
      eyebrow: 'Full-Stack Developer · Montreal, QC',
      headingAccent: 'Full-Stack Web Developer',
      headingTail: 'based in Montreal',
      lede: "I'm Mohame, i build custom websites, scalable web applications, and CMS-driven platforms for founders and teams. Precise engineering, end-to-end ownership, and interfaces that feel deliberate.",
      ctaStart: 'Start a project',
      ctaProjects: 'See Latest projects',
      statYears: 'Years Developing',
      statYearsValue: '3+',
      statStack: 'Stack span',
      statStackValue: 'Laravel · React · Typescript · Golang',
      statLocation: 'Location',
      statLocationValue: 'Montreal · Remote',
    },
    projects: {
      eyebrow: 'Projects',
      heading: 'Take a look at my latest projects',
      lede: 'Tap any card to open the full case study.',
      arrowKeysLabel: 'arrow keys to navigate',
      carouselAria: 'Featured projects',
      prevProject: 'Previous project',
      nextProject: 'Next project',
      openCaseStudy: 'Open {title} case study',
      viewCaseStudy: 'View case study',
      openLiveSite: 'Open {title} live site',
      visitLive: 'Visit live',
      visitLiveTooltip: 'Visit live site',
      screenshotsAria: '{title} screenshots',
      overview: 'Overview',
      highlights: 'Highlights',
      stack: 'Stack',
      role: 'Role',
      year: 'Year',
      status: 'Status',
    },
    resume: {
      eyebrow: 'Resume',
      headingStart: 'A concise record of',
      headingEnd: "what I've shipped.",
      lede: 'The full CV covers experience, stack, and a portfolio of production builds. Tap the preview to open it in a full reader, or grab the PDF for your records.',
      highlight1: 'Laravel · PHP · MySQL / PostgreSQL',
      highlight2: 'React · TypeScript · modern CSS',
      highlight3: 'WordPress · Django · Golang · Python',
      highlight4: 'Authentication, roles, and business logic',
      viewFull: 'View full resume',
      downloadPdf: 'Download PDF',
      openFull: 'Open full resume',
      previewTitle: 'Resume preview',
      clickExpand: 'Click to expand',
      pdfMeta: 'PDF · Updated 2025',
      modalTitle: 'Mohame Roach — Resume',
      modalSubtitle: 'Full CV · Updated 2025',
      download: 'Download',
      pdfAria: 'Mohame Roach resume (PDF)',
      iframeTitle: 'Mohame Roach resume',
      fallbackIntro: "Your browser can't display this PDF inline. You can ",
      fallbackOpenTab: 'open it in a new tab',
      fallbackOr: ' or ',
      fallbackDownload: 'download the file',
      fallbackEnd: '.',
    },
    faq: {
      eyebrow: 'Questions',
      heading: 'FAQ',
      lede: 'Find answers to common questions about my services, pricing, and process.',
      q_services: 'What kind of projects do you take on?',
      q_process: 'What does your process and timeline look like?',
      q_pricing: 'How do you price projects?',
      q_team: 'Do you work with existing teams and codebases?',
      q_quality: 'What does "built with care" actually mean?',
      q_after: 'What happens after the project launches?',
      q_getting_started: 'How do we get started?',
    },
    contact: {
      eyebrow: 'Contact',
      headingStart: 'Have a project in mind?',
      headingAccent: "Let's talk.",
      lede: "Share a few details about what you're building. I reply to every inquiry within 1–2 business days.",
      metaEmail: 'Email',
      metaLocation: 'Location',
      metaLocationValue: 'Montreal, QC · Remote-friendly',
      metaAvailability: 'Availability',
      metaAvailabilityValue: 'Open to freelance & contract',
      github: 'GitHub',
      linkedin: 'LinkedIn',
    },
    form: {
      nameLabel: 'Name',
      namePlaceholder: 'Your full name',
      emailLabel: 'Email',
      emailPlaceholder: 'you@company.com',
      companyLabel: 'Company / project',
      companyHint: 'Optional',
      companyPlaceholder: 'Acme Inc. or side project',
      budgetLabel: 'Estimated budget',
      timelineLabel: 'Timeline',
      messageLabel: 'Project details',
      messagePlaceholder: 'What are you building? Share goals, constraints, and any links that help.',
      honeypotLabel: 'Website (leave blank)',
      botCheckAria: 'Bot check',
      submitIdle: 'Send message',
      submitSending: 'Sending…',
      submitSent: 'Message sent',
      privacy: 'Your details are only used to reply to this inquiry. Protected by Cloudflare Turnstile, server-side validation, and rate limiting.',
      budget_under2k: 'Under $2,000',
      budget_2k5k: '$2,000 – $5,000',
      budget_5k10k: '$5,000 – $10,000',
      budget_10k25k: '$10,000 – $25,000',
      budget_25kplus: '$25,000+',
      budget_notsure: 'Not sure yet',
      timeline_asap: 'ASAP',
      timeline_1month: 'Within 1 month',
      timeline_1to3: '1 – 3 months',
      timeline_3plus: '3+ months',
      timeline_flexible: 'Flexible',
      err_name_short: 'Please enter your name (2+ characters).',
      err_name_long: 'Name is too long (max 80 characters).',
      err_email_required: 'Email is required.',
      err_email_invalid: 'Please enter a valid email address.',
      err_email_long: 'Email is too long.',
      err_message_short: 'Please share a bit more (at least 20 characters).',
      err_message_long: 'Message is too long (max 4,000 characters).',
      err_message_links: 'Too many links in the message.',
      err_company_long: 'Company name is too long.',
      err_spam: 'Spam detected.',
      toastCheckTitle: 'Check your inputs',
      toastCheckMsg: 'Please fix the highlighted fields and try again.',
      toastBotTitle: 'Bot check required',
      toastBotMsg: 'Please complete the bot check before sending your message.',
      toastSentTitle: 'Message sent',
      toastSentMsg: "Thanks — your message is in. I'll get back to you within 1–2 business days.",
      toastErrTitle: "Couldn't send message",
      toastErrMsg: 'Something went wrong. Please try again or email directly.',
    },
    footer: {
      brandAria: 'Mohame Roach — home',
      tag: 'Full-stack developer | Montreal, QC.',
      copyright: '© {year} Mohame Roach. All rights reserved.',
      col_connect: 'Connect',
      col_site: 'Site',
      col_resources: 'Resources',
      footerNavAria: 'Footer',
      link_contact: 'Contact',
      link_email: 'Email',
      link_github: 'GitHub',
      link_linkedin: 'LinkedIn',
      link_x: 'X',
      link_projects: 'Projects',
      link_resume: 'Resume',
      link_faq: 'FAQ',
      link_start: 'Start a project',
      link_downloadCv: 'Download CV',
      link_source: 'Source code',
      link_colophon: 'Colophon',
      link_privacy: 'Privacy',
      theme_system: 'Use system theme',
      theme_light: 'Use light theme',
      theme_dark: 'Use dark theme',
      themeGroup: 'Theme',
    },
    toast: {
      viewport: 'Notifications',
      dismiss: 'Dismiss notification',
    },
  },
  fr: {
    meta: {
      homeTitle: 'Développeur web full-stack à Montréal',
      skipToContent: 'Aller au contenu',
    },
    header: {
      brandAria: 'Mohame Roach — accueil',
      primaryNavAria: 'Principale',
      mobileNavAria: 'Principale mobile',
      openMenu: 'Ouvrir le menu',
      closeMenu: 'Fermer le menu',
      projects: 'Projets',
      blog: 'Blog',
      resume: 'CV',
      faq: 'FAQ',
      contact: 'Contact',
      admin: 'Admin',
      switchLanguageTo: 'Changer la langue pour {lang}',
      languageGroup: 'Langue',
    },
    hero: {
      eyebrow: 'Développeur full-stack · Montréal, QC',
      headingAccent: 'Développeur web full-stack',
      headingTail: 'basé à Montréal',
      lede: "Je m'appelle Mohame, je conçois des sites sur mesure, des applications web évolutives et des plateformes CMS pour des fondateurs et des équipes. Une ingénierie précise, une prise en charge de bout en bout et des interfaces pensées dans le détail.",
      ctaStart: 'Démarrer un projet',
      ctaProjects: 'Voir les derniers projets',
      statYears: "Années d'expérience",
      statYearsValue: '3+',
      statStack: 'Pile technique',
      statStackValue: 'Laravel · React · PG',
      statLocation: 'Localisation',
      statLocationValue: 'Montréal · À distance',
    },
    projects: {
      eyebrow: 'Projets',
      heading: 'Découvrez mes derniers projets',
      lede: 'Touchez une carte pour ouvrir l’étude de cas complète.',
      arrowKeysLabel: 'flèches pour naviguer',
      carouselAria: 'Projets en vedette',
      prevProject: 'Projet précédent',
      nextProject: 'Projet suivant',
      openCaseStudy: "Ouvrir l'étude de cas de {title}",
      viewCaseStudy: "Voir l'étude de cas",
      openLiveSite: 'Ouvrir le site en ligne de {title}',
      visitLive: 'Voir en ligne',
      visitLiveTooltip: 'Visiter le site en ligne',
      screenshotsAria: 'Captures d’écran de {title}',
      overview: 'Vue d’ensemble',
      highlights: 'Points forts',
      stack: 'Technologies',
      role: 'Rôle',
      year: 'Année',
      status: 'Statut',
    },
    resume: {
      eyebrow: 'CV',
      headingStart: 'Un aperçu concis de',
      headingEnd: "ce que j'ai livré.",
      lede: 'Le CV complet détaille l’expérience, la pile technique et un portfolio de réalisations en production. Touchez l’aperçu pour l’ouvrir en grand, ou téléchargez le PDF.',
      highlight1: 'Laravel · PHP · MySQL / PostgreSQL',
      highlight2: 'React · TypeScript · CSS moderne',
      highlight3: 'WordPress · Django · Golang · Python',
      highlight4: 'Authentification, rôles et logique métier',
      viewFull: 'Voir le CV complet',
      downloadPdf: 'Télécharger le PDF',
      openFull: 'Ouvrir le CV complet',
      previewTitle: 'Aperçu du CV',
      clickExpand: 'Cliquer pour agrandir',
      pdfMeta: 'PDF · Mis à jour 2025',
      modalTitle: 'Mohame Roach — CV',
      modalSubtitle: 'CV complet · Mis à jour 2025',
      download: 'Télécharger',
      pdfAria: 'CV de Mohame Roach (PDF)',
      iframeTitle: 'CV de Mohame Roach',
      fallbackIntro: "Votre navigateur ne peut pas afficher ce PDF. Vous pouvez ",
      fallbackOpenTab: 'l’ouvrir dans un nouvel onglet',
      fallbackOr: ' ou ',
      fallbackDownload: 'télécharger le fichier',
      fallbackEnd: '.',
    },
    faq: {
      eyebrow: 'Questions',
      heading: 'FAQ',
      lede: 'Réponses aux questions courantes sur mes services, mes tarifs et mon processus.',
      q_services: 'Quels types de projets prenez-vous en charge ?',
      q_process: 'À quoi ressemble votre processus et votre calendrier ?',
      q_pricing: 'Comment tarifez-vous vos projets ?',
      q_team: 'Travaillez-vous avec des équipes et des bases de code existantes ?',
      q_quality: 'Que signifie vraiment « fait avec soin » ?',
      q_after: 'Que se passe-t-il après le lancement du projet ?',
      q_getting_started: 'Comment démarrons-nous ?',
    },
    contact: {
      eyebrow: 'Contact',
      headingStart: 'Un projet en tête ?',
      headingAccent: 'Discutons-en.',
      lede: 'Partagez quelques détails sur ce que vous construisez. Je réponds à chaque message sous 1 à 2 jours ouvrés.',
      metaEmail: 'Courriel',
      metaLocation: 'Localisation',
      metaLocationValue: 'Montréal, QC · À distance',
      metaAvailability: 'Disponibilité',
      metaAvailabilityValue: 'Ouvert aux missions freelance & contrat',
      github: 'GitHub',
      linkedin: 'LinkedIn',
    },
    form: {
      nameLabel: 'Nom',
      namePlaceholder: 'Votre nom complet',
      emailLabel: 'Courriel',
      emailPlaceholder: 'vous@entreprise.com',
      companyLabel: 'Entreprise / projet',
      companyHint: 'Optionnel',
      companyPlaceholder: 'Acme Inc. ou projet perso',
      budgetLabel: 'Budget estimé',
      timelineLabel: 'Échéance',
      messageLabel: 'Détails du projet',
      messagePlaceholder: 'Que construisez-vous ? Partagez objectifs, contraintes et liens utiles.',
      honeypotLabel: 'Site web (laisser vide)',
      botCheckAria: 'Vérification anti-robot',
      submitIdle: 'Envoyer le message',
      submitSending: 'Envoi…',
      submitSent: 'Message envoyé',
      privacy: 'Vos coordonnées ne servent qu’à répondre à ce message. Protégé par Cloudflare Turnstile, validation serveur et limitation de débit.',
      budget_under2k: 'Moins de 2 000 $',
      budget_2k5k: '2 000 $ – 5 000 $',
      budget_5k10k: '5 000 $ – 10 000 $',
      budget_10k25k: '10 000 $ – 25 000 $',
      budget_25kplus: '25 000 $ et plus',
      budget_notsure: 'Pas encore sûr',
      timeline_asap: 'Dès que possible',
      timeline_1month: 'Moins d’un mois',
      timeline_1to3: '1 à 3 mois',
      timeline_3plus: '3 mois et plus',
      timeline_flexible: 'Flexible',
      err_name_short: 'Veuillez entrer votre nom (au moins 2 caractères).',
      err_name_long: 'Le nom est trop long (max. 80 caractères).',
      err_email_required: 'Le courriel est requis.',
      err_email_invalid: 'Veuillez entrer une adresse courriel valide.',
      err_email_long: 'Le courriel est trop long.',
      err_message_short: 'Veuillez en dire un peu plus (au moins 20 caractères).',
      err_message_long: 'Le message est trop long (max. 4 000 caractères).',
      err_message_links: 'Trop de liens dans le message.',
      err_company_long: 'Le nom d’entreprise est trop long.',
      err_spam: 'Spam détecté.',
      toastCheckTitle: 'Vérifiez vos informations',
      toastCheckMsg: 'Veuillez corriger les champs en rouge et réessayer.',
      toastBotTitle: 'Vérification requise',
      toastBotMsg: 'Veuillez compléter la vérification anti-robot avant d’envoyer.',
      toastSentTitle: 'Message envoyé',
      toastSentMsg: 'Merci — votre message est bien reçu. Je vous réponds sous 1 à 2 jours ouvrés.',
      toastErrTitle: 'Envoi impossible',
      toastErrMsg: 'Une erreur est survenue. Réessayez ou écrivez directement par courriel.',
    },
    footer: {
      brandAria: 'Mohame Roach — accueil',
      tag: 'Développeur full-stack — Montréal, QC.',
      copyright: '© {year} Mohame Roach. Tous droits réservés.',
      col_connect: 'Contact',
      col_site: 'Site',
      col_resources: 'Ressources',
      footerNavAria: 'Pied de page',
      link_contact: 'Contact',
      link_email: 'Courriel',
      link_github: 'GitHub',
      link_linkedin: 'LinkedIn',
      link_x: 'X',
      link_projects: 'Projets',
      link_resume: 'CV',
      link_faq: 'FAQ',
      link_start: 'Démarrer un projet',
      link_downloadCv: 'Télécharger le CV',
      link_source: 'Code source',
      link_colophon: 'Colophon',
      link_privacy: 'Confidentialité',
      theme_system: 'Utiliser le thème système',
      theme_light: 'Utiliser le thème clair',
      theme_dark: 'Utiliser le thème sombre',
      themeGroup: 'Thème',
    },
    toast: {
      viewport: 'Notifications',
      dismiss: 'Masquer la notification',
    },
  },
};
